/* 0.48.63 - A Modal of Pages and forms: a Column group shown as the
 * canonical Henaket modal window (the ConfirmDialog's look), opened and
 * closed by rules.
 *
 * Its children stay MOUNTED while it is closed, only not shown. That
 * matters: a file chosen in a modal, or a value typed there, must still
 * be readable by the rule that runs when OK is pressed, and a File upload
 * clears what it published when it unmounts. So open and closed are the
 * same element tree, with display switched.
 *
 * OK and Cancel (and the x and Escape, which count as Cancel) report a
 * click on "<id>.ok" / "<id>.cancel", read by rules as "<id>.ok was just
 * clicked". OK closes the modal unless the admin turned that off (a rule
 * then closes it, for example after checking the fields).
 */
import { useEffect, useRef } from 'react';
import styles from './ConfirmDialog.module.css';
import buttonStyles from './Button.module.css';
import pm from './PageModal.module.css';

export default function PageModal({
  open, title, okLabel, cancelLabel, closeLabel, width, hideCancel,
  onOk, onCancel, children, id,
}) {
  const boxRef = useRef(null);
  /* 0.48.81 - Read through a ref: onCancel is a new function on every
     render of the page, and as an effect dependency it re-ran the effect
     below on every keystroke, sending the focus back to the first field
     while typing in another. */
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); cancelRef.current?.(); }
    };
    document.addEventListener('keydown', handler);
    /* The page behind does not scroll while the modal is open.
       0.49.15 - Locked for real: overflow on the body alone let the page
       scroll on phones and, with the mouse outside the window, on large
       screens too (the page scrolls on <html>, which keeps its scrollbar
       so the layout does not shift). The body is held in place where it
       was and put back on close. */
    const html = document.documentElement;
    const body = document.body;
    const y = window.scrollY || html.scrollTop || 0;
    const prev = { htmlOverflow: html.style.overflow, overflow: body.style.overflow,
      position: body.style.position, top: body.style.top, left: body.style.left,
      right: body.style.right, width: body.style.width };
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${y}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    /* A wheel or a swipe anywhere on the modal or the dark backdrop
       scrolls the modal's content, not the page. */
    const backdrop = boxRef.current ? boxRef.current.parentElement : null;
    const content = () => (boxRef.current ? boxRef.current.querySelector('[data-modal-body]') : null);
    const inContent = (t) => { const c = content(); return !!(c && t && c.contains(t)); };
    const onWheel = (e) => {
      if (inContent(e.target)) return;
      const c = content();
      if (!c) return;
      e.preventDefault();
      c.scrollTop += e.deltaY;
    };
    let touchY = null;
    const onTouchStart = (e) => { touchY = e.touches && e.touches[0] ? e.touches[0].clientY : null; };
    const onTouchMove = (e) => {
      if (inContent(e.target) || touchY === null || !e.touches || !e.touches[0]) return;
      const c = content();
      if (!c) return;
      const ny = e.touches[0].clientY;
      e.preventDefault();
      c.scrollTop += touchY - ny;
      touchY = ny;
    };
    if (backdrop) {
      backdrop.addEventListener('wheel', onWheel, { passive: false });
      backdrop.addEventListener('touchstart', onTouchStart, { passive: true });
      backdrop.addEventListener('touchmove', onTouchMove, { passive: false });
    }
    // Focus the first field, or the box itself. 0.48.81 - A field drawn
    // as a button (a dropdown, a Cascade's parts) counts as a field; the
    // window's own x and buttons do not, and a part that says so is
    // passed over (an open Cascade's summary line, for its first part).
    const t = window.setTimeout(() => {
      const box = boxRef.current;
      if (!box) return;
      const body = box.querySelector('[data-modal-body]') || box;
      const first = Array.from(body.querySelectorAll(
        'input:not([type=hidden]):not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled])'))
        .find(n => !n.closest('[data-autofocus-skip]') && n.offsetParent !== null);
      (first || box).focus?.({ preventScroll: true });
    }, 0);
    return () => {
      document.removeEventListener('keydown', handler);
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.overflow;
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      window.scrollTo(0, y);
      if (backdrop) {
        backdrop.removeEventListener('wheel', onWheel);
        backdrop.removeEventListener('touchstart', onTouchStart);
        backdrop.removeEventListener('touchmove', onTouchMove);
      }
      window.clearTimeout(t);
    };
  }, [open]);

  const titleId = id ? `modal-title-${id}` : undefined;
  return (
    <div className={`${styles.backdrop} ${pm.backdrop}`} style={open ? undefined : { display: 'none' }}
         role="dialog" aria-modal="true" aria-labelledby={titleId} aria-hidden={open ? undefined : 'true'}
         data-page-modal={id || ''}>
      <div className={`${styles.modal} ${pm.modal}`} ref={boxRef} tabIndex={-1}
           style={width ? { width: `${width}px` } : undefined}>
        <div className={styles.headerRow}>
          <h2 id={titleId} className={styles.title}>{title}</h2>
          <button type="button" className={styles.topClose} onClick={onCancel} aria-label={closeLabel}>
            <svg className={styles.topCloseIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 5 L19 19 M19 5 L5 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span className={styles.topCloseText}>{closeLabel}</span>
          </button>
        </div>
        <div className={pm.body} data-modal-body="">
          {children}
        </div>
        <div className={styles.actions}>
          <button type="button" onClick={onOk}
                  className={`${buttonStyles.button} ${buttonStyles.primary}`}>
            {okLabel}
          </button>
          {!hideCancel && (
            <button type="button" onClick={onCancel}
                    className={`${buttonStyles.button} ${buttonStyles.secondary}`}>
              {cancelLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
